import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import TitleCard from '@/components/feature/TitleCard';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { getRelated, getTitleById } from '@/lib/catalog';

export default function Detail() {
  const { id } = useParams();
  const item = getTitleById(id);
  const [showDemo, setShowDemo] = useState(false);

  useDocumentMeta(
    item ? `星辰影院-影视详情-${item.title}` : '星辰影院-影视资源不存在',
    item
      ? `${item.title}（${item.year}，${item.region}）简介与详细信息：${item.summary.slice(0, 60)}`
      : '该影视资源不存在或已下架，请返回首页浏览更多电影与电视剧。',
    item ? `${item.title},${item.genres.join(',')},影视详情,星辰影院` : '影视资源,星辰影院',
  );

  if (!item) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="flex min-h-[70vh] flex-col items-center justify-center px-4 pt-16 text-center">
          <span className="flex h-20 w-20 items-center justify-center rounded-full bg-secondary-100 text-secondary-600">
            <i className="ri-movie-line text-4xl" />
          </span>
          <h1 className="mt-6 font-heading text-2xl font-bold text-foreground-950">
            影视资源不存在或已下架
          </h1>
          <p className="mt-3 max-w-md text-sm text-foreground-500">
            你访问的影视作品可能已被移除，或链接有误。不妨回到首页看看更多精彩内容。
          </p>
          <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/"
              className="whitespace-nowrap rounded-md bg-primary-600 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-700"
            >
              返回首页
            </Link>
            <Link
              to="/movies"
              className="whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-6 py-3 text-sm font-semibold text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
            >
              浏览电影
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const isTv = item.type === 'tv';
  const related = getRelated(item, 6);
  const typeLabel = isTv ? '电视剧' : '电影';
  const typePath = isTv ? '/tvshows' : '/movies';

  const infoRows = [
    { label: '类型', value: item.genres.join(' / ') },
    { label: '地区', value: item.region },
    { label: '上映年份', value: `${item.year} 年` },
    isTv
      ? { label: '季数 / 集数', value: `${item.seasons} 季 · ${item.episodes} 集` }
      : { label: '片长', value: item.duration || '—' },
    { label: '导演', value: item.director },
    { label: '主演', value: item.cast.join('、') },
    { label: '评分', value: `${item.rating.toFixed(1)} 分` },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="pt-16 md:pt-[72px]">
        {/* hero */}
        <section className="relative overflow-hidden bg-primary-950">
          <img
            src={item.backdrop}
            alt={`${item.title} 剧照`}
            title={`${item.title} - 星辰影院`}
            className="absolute inset-0 h-full w-full object-cover object-top opacity-70"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950 via-primary-950/85 to-primary-950/50" />
          <div className="absolute inset-0 bg-gradient-to-t from-primary-950 to-transparent" />

          <div className="relative mx-auto max-w-[1280px] px-4 py-10 md:px-8 md:py-14">
            <nav className="flex flex-wrap items-center gap-2 text-xs text-background-400" aria-label="面包屑">
              <Link to="/" className="transition-colors hover:text-accent-300">
                首页
              </Link>
              <i className="ri-arrow-right-s-line" />
              <Link to={typePath} className="transition-colors hover:text-accent-300">
                {typeLabel}
              </Link>
              <i className="ri-arrow-right-s-line" />
              <span className="text-background-200">{item.title}</span>
            </nav>

            <div className="mt-6 flex flex-col gap-6 md:flex-row md:gap-8">
              <div className="w-40 shrink-0 overflow-hidden rounded-lg border border-primary-800 sm:w-48 md:w-60">
                <img
                  src={item.poster}
                  alt={`${item.title} 海报`}
                  title={`${item.title} - 星辰影院`}
                  className="aspect-[2/3] h-full w-full object-cover object-top"
                />
              </div>

              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-2.5">
                  <span className="rounded-md bg-accent-500 px-2.5 py-1 text-xs font-bold text-primary-950">
                    {typeLabel}
                  </span>
                  <span className="flex items-center gap-1 rounded-md bg-black/40 px-2.5 py-1 text-xs font-medium text-background-100 backdrop-blur-sm">
                    <i className="ri-star-fill text-accent-400" />
                    {item.rating.toFixed(1)}
                  </span>
                </div>

                <h1 className="mt-3 font-heading text-2xl font-extrabold text-background-50 md:text-4xl">
                  {item.title}
                </h1>

                <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-sm text-background-300">
                  <span>{item.year}</span>
                  <span className="text-background-600">·</span>
                  <span>{item.region}</span>
                  <span className="text-background-600">·</span>
                  <span>{isTv ? `${item.seasons}季 ${item.episodes}集` : item.duration}</span>
                  <span className="text-background-600">·</span>
                  <span>{item.genres.join(' / ')}</span>
                </div>

                <p className="mt-4 max-w-2xl text-sm leading-relaxed text-background-300">
                  {item.summary}
                </p>

                <div className="mt-7 flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setShowDemo(true)}
                    className="flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-semibold text-primary-950 transition-colors hover:bg-accent-400"
                  >
                    <i className="ri-play-fill text-lg" />
                    立即观看（演示）
                  </button>
                  <Link
                    to={typePath}
                    className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-50/40 bg-black/25 px-6 py-3 text-sm font-semibold text-background-50 backdrop-blur-sm transition-colors hover:bg-black/40"
                  >
                    <i className="ri-compass-3-line text-lg" />
                    更多{typeLabel}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* body */}
        <section className="mx-auto max-w-[1280px] px-4 py-10 md:px-8 md:py-14">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <div className="flex items-center gap-2.5">
                <span className="h-6 w-1.5 rounded-full bg-accent-500" />
                <h2 className="font-heading text-xl font-bold text-foreground-950">剧情简介</h2>
              </div>
              <p className="mt-4 text-sm leading-7 text-foreground-700 md:text-base">{item.summary}</p>

              <div className="mt-10 flex items-center gap-2.5">
                <span className="h-6 w-1.5 rounded-full bg-accent-500" />
                <h2 className="font-heading text-xl font-bold text-foreground-950">详细信息</h2>
              </div>
              <dl className="mt-4 grid grid-cols-1 gap-px overflow-hidden rounded-lg border border-background-200 bg-background-200 sm:grid-cols-2">
                {infoRows.map((row) => (
                  <div key={row.label} className="flex gap-4 bg-background-50 px-4 py-3.5">
                    <dt className="w-20 shrink-0 text-sm text-foreground-500">{row.label}</dt>
                    <dd className="text-sm font-medium text-foreground-800">{row.value}</dd>
                  </div>
                ))}
              </dl>

              <div className="mt-8 rounded-lg border border-secondary-200 bg-secondary-50 p-4">
                <p className="flex items-start gap-2 text-xs leading-relaxed text-secondary-900">
                  <i className="ri-information-line mt-0.5 text-base" />
                  本站为演示站点，所有影视作品及数据均为示例内容，播放功能仅作界面展示，不接入真实流媒体服务。
                </p>
              </div>
            </div>

            {/* aside */}
            <aside className="space-y-6">
              <div className="rounded-xl border border-background-200 bg-background-50 p-5">
                <h3 className="text-sm font-bold text-foreground-900">所属分类</h3>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Link
                    to={typePath}
                    className="rounded-full bg-primary-100 px-3 py-1.5 text-xs font-medium text-primary-700 transition-colors hover:bg-primary-200"
                  >
                    {typeLabel}
                  </Link>
                  {item.genres.map((genre) => (
                    <Link
                      key={genre}
                      to={`${typePath}?genre=${encodeURIComponent(genre)}`}
                      className="rounded-full bg-secondary-100 px-3 py-1.5 text-xs font-medium text-secondary-900 transition-colors hover:bg-secondary-200"
                    >
                      {genre}
                    </Link>
                  ))}
                </div>
              </div>

              <div className="rounded-xl border border-background-200 bg-background-50 p-5">
                <h3 className="text-sm font-bold text-foreground-900">查看方式</h3>
                <p className="mt-3 text-xs leading-relaxed text-foreground-500">
                  可通过分类页按类型、年份、地区筛选，或在顶部搜索框输入片名快速定位。
                </p>
                <div className="mt-4 flex flex-col gap-2">
                  <Link
                    to="/search"
                    className="flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-600 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700"
                  >
                    <i className="ri-search-line" />
                    搜索影视
                  </Link>
                  <Link
                    to={typePath}
                    className="flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
                  >
                    <i className="ri-equalizer-line" />
                    按条件筛选
                  </Link>
                </div>
              </div>
            </aside>
          </div>

          {/* related */}
          <div className="mt-14">
            <div className="mb-6 flex items-center gap-2.5">
              <span className="h-6 w-1.5 rounded-full bg-accent-500" />
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                相关推荐
              </h2>
            </div>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-6">
              {related.map((rel) => (
                <TitleCard key={rel.id} item={rel} />
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />

      {showDemo && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-primary-950/70 px-4 backdrop-blur-sm"
          onClick={() => setShowDemo(false)}
        >
          <div
            className="w-full max-w-sm rounded-xl border border-background-200 bg-background-50 p-6 text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-accent-100 text-accent-700">
              <i className="ri-movie-2-line text-3xl" />
            </span>
            <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">演示播放</h3>
            <p className="mt-2 text-sm leading-relaxed text-foreground-600">
              本站为演示站点，播放功能仅作界面展示，暂不接入真实流媒体内容。感谢你的理解。
            </p>
            <button
              type="button"
              onClick={() => setShowDemo(false)}
              className="mt-5 w-full whitespace-nowrap rounded-md bg-primary-600 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-700"
            >
              我知道了
            </button>
          </div>
        </div>
      )}
    </div>
  );
}